import {
  LIMITS,
  VALID_INPUT_TYPES,
  RESERVED_FIELD_NAMES,
  SCREEN_ID_REGEX,
  FIELD_NAME_REGEX,
  INPUT_TYPES,
  LAYOUT_TEXT_TYPES,
} from "./flowConstants";

const ALL_VALID_LAYOUT_TYPES = [...LAYOUT_TEXT_TYPES, "Form", "Footer"];
const ALL_VALID_FORM_TYPES = [
  ...INPUT_TYPES,
  "TextHeading", "TextSubheading", "TextBody", "TextCaption", "RichText",
  "Footer",
];
const VALID_DATA_TYPES = ["string", "number", "boolean", "array"];

/**
 * Validate a complete WhatsApp Flow JSON structure.
 */
export function validateFlow(flow) {
  const errors = [];
  const warnings = [];

  if (!flow) {
    errors.push("Flow is empty");
    return { valid: false, errors, warnings };
  }

  // Auto-mapping note: payload/data are generated during export (buildFlowJSON).
  // Validation here focuses on structural correctness and expression usage in user-entered text.

  if (!["5.0", "5.1"].includes(flow.version)) {
    errors.push(`Version must be "5.0" or "5.1". Got "${flow.version}"`);
  }

  const screens = flow.screens || [];
  if (screens.length < LIMITS.MIN_SCREENS) {
    errors.push("Flow must have at least 1 screen");
  }
  if (screens.length > LIMITS.MAX_SCREENS) {
    errors.push(`Flow can have at most ${LIMITS.MAX_SCREENS} screens. Got ${screens.length}`);
  }

  const jsonStr = JSON.stringify(flow);
  const sizeKb = new Blob([jsonStr]).size / 1024;
  if (sizeKb > LIMITS.MAX_JSON_SIZE_KB) {
    errors.push(`Flow JSON exceeds ${LIMITS.MAX_JSON_SIZE_KB} KB (${sizeKb.toFixed(1)} KB)`);
  }

  const screenIds = new Set();
  const duplicateIds = new Set();
  screens.forEach((s) => {
    if (screenIds.has(s.id)) duplicateIds.add(s.id);
    screenIds.add(s.id);
  });
  if (duplicateIds.size > 0) {
    errors.push(`Duplicate screen IDs: ${[...duplicateIds].join(", ")}`);
  }

  const hasTerminal = screens.some((s) => s.terminal === true);
  if (screens.length > 0 && !hasTerminal) {
    errors.push("Flow must have at least one terminal screen");
  }

  // Dead screen check
  if (screens.length > 1) {
    const reachable = new Set();
    if (screens.length > 0) reachable.add(screens[0].id);
    screens.forEach((s) => {
      getNavigationTargets(s).forEach((t) => reachable.add(t));
    });
    screens.forEach((s) => {
      if (!reachable.has(s.id) && s.id !== screens[0]?.id) {
        warnings.push(`Screen "${s.id}" is unreachable (no screen navigates to it)`);
      }
    });
  }

  screens.forEach((screen, idx) => {
    validateScreen(screen, idx, screenIds, errors, warnings);
  });

  // Cross-screen contract: navigate payload keys should exist in target screen.data
  // In auto-mapping mode, payload/data contracts are injected by buildFlowJSON.
  // If importing externally-authored JSON, payload/data mismatches are not enforced here.

  return { valid: errors.length === 0, errors, warnings };
}

function getNavigationTargets(screen) {
  const targets = [];
  const children = screen.layout?.children || [];
  children.forEach((child) => {
    if (child.type === "Form") {
      (child.children || []).forEach((fc) => {
        if (fc.type === "Footer" && fc["on-click-action"]?.name === "navigate") {
          const t = fc["on-click-action"]?.next?.name;
          if (t) targets.push(t);
        }
      });
    }
    if (child.type === "Footer" && child["on-click-action"]?.name === "navigate") {
      const t = child["on-click-action"]?.next?.name;
      if (t) targets.push(t);
    }
  });
  return targets;
}

// ─── Screen validation ───

function validateScreen(screen, idx, allScreenIds, errors, warnings) {
  const prefix = `Screen ${idx + 1}`;

  // Rule 11: Screen ID
  if (!screen.id) {
    errors.push(`${prefix}: Missing screen ID`);
  } else {
    if (screen.id.length > LIMITS.SCREEN_ID_MAX_LEN)
      errors.push(`${prefix}: ID "${screen.id}" exceeds ${LIMITS.SCREEN_ID_MAX_LEN} chars`);
    if (!SCREEN_ID_REGEX.test(screen.id))
      errors.push(`${prefix}: ID "${screen.id}" — only A-Z, a-z, _ allowed (no numbers or spaces)`);
  }

  if (screen.title && screen.title.length > LIMITS.SCREEN_TITLE_MAX_LEN) {
    errors.push(`${prefix}: Title exceeds ${LIMITS.SCREEN_TITLE_MAX_LEN} chars`);
  }

  const dataSpec = screen.data || {};
  if (dataSpec && typeof dataSpec !== "object") {
    errors.push(`${prefix}: "data" must be an object`);
  } else {
    Object.entries(dataSpec).forEach(([k, cfg]) => {
      if (!FIELD_NAME_REGEX.test(k)) {
        errors.push(`${prefix}: data key "${k}" contains invalid characters`);
      }
      if (!cfg || typeof cfg !== "object") {
        errors.push(`${prefix}: data key "${k}" must map to an object with "type"`);
        return;
      }
      if (!VALID_DATA_TYPES.includes(cfg.type)) {
        errors.push(`${prefix}: data key "${k}" has invalid type "${cfg.type}"`);
      }
    });
  }

  // Rule 15: Single layout
  if (!screen.layout) { errors.push(`${prefix}: Missing layout`); return; }
  if (screen.layout.type !== "SingleColumnLayout") {
    errors.push(`${prefix}: Layout type must be "SingleColumnLayout"`);
  }

  const layoutChildren = screen.layout.children || [];

  // Rule 10: Component count per screen
  let totalComponents = 0;
  layoutChildren.forEach((child) => {
    totalComponents++;
    if (child.type === "Form") totalComponents += (child.children || []).length;
  });
  if (totalComponents > LIMITS.MAX_COMPONENTS_PER_SCREEN) {
    errors.push(`${prefix}: Too many components (${totalComponents}/${LIMITS.MAX_COMPONENTS_PER_SCREEN})`);
  }

  // Rule 12: Max 1 form per screen
  const forms = layoutChildren.filter((c) => c.type === "Form");
  if (forms.length > 1) {
    errors.push(`${prefix}: Maximum 1 form per screen, found ${forms.length}`);
  }

  // Rule 4: Input components must be inside a Form (not at layout level)
  layoutChildren.forEach((child, ci) => {
    if (INPUT_TYPES.includes(child.type)) {
      errors.push(`${prefix}: "${child.type}" must be inside a Form, not at layout level (component ${ci + 1})`);
    }
  });

  // Rule 15: Only one Footer per screen (either inside Form OR outside Form)
  const footerOutside = layoutChildren.filter((c) => c.type === "Footer").length;
  const footerInside = layoutChildren
    .filter((c) => c.type === "Form")
    .flatMap((f) => f.children || [])
    .filter((c) => c.type === "Footer").length;
  if (footerOutside + footerInside > 1) {
    errors.push(
      `${prefix}: Only one Footer is allowed per screen (either inside Form or outside Form), found ${footerOutside + footerInside}`
    );
  }

  // Rule 1 (layout-level): Footer must be the last layout child if present
  const lastFooterIdx = findLastIndex(layoutChildren, (c) => c.type === "Footer");
  if (lastFooterIdx >= 0 && lastFooterIdx < layoutChildren.length - 1) {
    errors.push(`${prefix}: Footer must be the last component in the layout`);
  }

  // Validate each layout child
  layoutChildren.forEach((child, ci) => {
    const cPrefix = `${prefix} → Layout[${ci + 1}]`;
    if (LAYOUT_TEXT_TYPES.includes(child.type)) {
      validateTextBlock(child, cPrefix, errors);
    } else if (child.type === "Form") {
      validateForm(child, screen, allScreenIds, prefix, errors, warnings);
    } else if (child.type === "Footer") {
      validateFooter(child, screen, allScreenIds, cPrefix, errors);
    } else if (!ALL_VALID_LAYOUT_TYPES.includes(child.type) && !INPUT_TYPES.includes(child.type)) {
      warnings.push(`${cPrefix}: Unknown layout component "${child.type}"`);
    }
  });

  // Rule 5: Terminal screen — must have at least one Footer with "complete"
  if (screen.terminal) {
    const navTargets = getNavigationTargets(screen);
    if (navTargets.length > 0) {
      errors.push(`${prefix}: Terminal screen cannot have navigate actions`);
    }
    const hasComplete = hasFooterAction(screen, "complete");
    if (!hasComplete) {
      errors.push(`${prefix}: Terminal screen must have a Footer with "complete" action`);
    }
  }

  // Rule 6: Non-terminal screen must navigate
  if (!screen.terminal && screens_count_gt_one(allScreenIds)) {
    const hasNav = hasFooterAction(screen, "navigate");
    if (!hasNav) {
      warnings.push(`${prefix}: Non-terminal screen should have a Footer with "navigate" action`);
    }
  }

  // RichText rule: must be the only component on the screen
  validateRichTextOnlyRule(screen, prefix, errors);
}

function screens_count_gt_one(allScreenIds) {
  return allScreenIds.size > 1;
}

function hasFooterAction(screen, actionName) {
  const layoutChildren = screen.layout?.children || [];
  for (const child of layoutChildren) {
    if (child.type === "Footer" && child["on-click-action"]?.name === actionName) return true;
    if (child.type === "Form") {
      for (const fc of (child.children || [])) {
        if (fc.type === "Footer" && fc["on-click-action"]?.name === actionName) return true;
      }
    }
  }
  return false;
}

function validateRichTextOnlyRule(screen, prefix, errors) {
  const layoutChildren = screen.layout?.children || [];
  const types = [];

  layoutChildren.forEach((child) => {
    if (child.type === "Form") {
      (child.children || []).forEach((fc) => types.push(fc.type));
        } else {
      types.push(child.type);
    }
  });

  const hasRich = types.includes("RichText");
  if (!hasRich) return;

  const nonRich = types.filter((t) => t && t !== "RichText");
  if (nonRich.length > 0) {
    errors.push(`${prefix}: RichText cannot be used with other components on the same screen`);
  }
}

function findLastIndex(arr, fn) {
  for (let i = arr.length - 1; i >= 0; i--) {
    if (fn(arr[i])) return i;
  }
  return -1;
}

// ─── Form validation ───

function validateForm(form, screen, allScreenIds, prefix, errors, warnings) {
  // Rule 12: Form name
  if (!form.name) {
    errors.push(`${prefix}: Form is missing a name`);
  } else {
    if (form.name.length > LIMITS.FORM_NAME_MAX_LEN)
      errors.push(`${prefix}: Form name exceeds ${LIMITS.FORM_NAME_MAX_LEN} chars`);
    if (!FIELD_NAME_REGEX.test(form.name))
      errors.push(`${prefix}: Form name "${form.name}" contains invalid characters`);
  }

  const children = form.children || [];

  if (children.length > LIMITS.MAX_COMPONENTS_PER_FORM) {
    errors.push(`${prefix}: Form has ${children.length} components (max ${LIMITS.MAX_COMPONENTS_PER_FORM})`);
  }

  // Rule 3: No nested forms
  children.forEach((comp, ci) => {
    if (comp.type === "Form") {
      errors.push(`${prefix} → Component ${ci + 1}: Forms cannot be nested`);
    }
  });

  // Rule 2: Only 1 Footer per form
  const footerCount = children.filter((c) => c.type === "Footer").length;
  if (footerCount > 1) {
    errors.push(`${prefix}: Form can have only 1 Footer, found ${footerCount}`);
  }

  // Rule 1: Footer must be last inside form
  const lastFooterIdx = findLastIndex(children, (c) => c.type === "Footer");
  if (lastFooterIdx >= 0 && lastFooterIdx < children.length - 1) {
    errors.push(`${prefix}: Footer must be the last component inside the Form`);
  }

  // Rule 13: Unique field names
  const fieldNames = new Set();

  children.forEach((comp, ci) => {
    const cPrefix = `${prefix} → Component ${ci + 1}`;

    if (comp.name) {
      if (fieldNames.has(comp.name))
        errors.push(`${cPrefix}: Duplicate field name "${comp.name}"`);
      fieldNames.add(comp.name);

      if (!FIELD_NAME_REGEX.test(comp.name))
        errors.push(`${cPrefix}: Field name "${comp.name}" contains invalid characters`);
      if (RESERVED_FIELD_NAMES.includes(comp.name.toLowerCase()))
        errors.push(`${cPrefix}: "${comp.name}" is a reserved keyword`);
      if (comp.name.length > LIMITS.TEXT_INPUT_NAME_MAX)
        errors.push(`${cPrefix}: Field name exceeds ${LIMITS.TEXT_INPUT_NAME_MAX} chars`);
    }

    switch (comp.type) {
      case "TextInput":
        validateTextInput(comp, cPrefix, errors); break;
      case "Dropdown":
        validateDropdown(comp, cPrefix, errors); break;
      case "RadioButtonsGroup":
        validateRadioButtons(comp, cPrefix, errors); break;
      case "CheckboxGroup":
        validateCheckboxGroup(comp, cPrefix, errors); break;
      case "DatePicker":
        validateDatePicker(comp, cPrefix, errors); break;
      case "TextHeading":
      case "TextSubheading":
      case "TextBody":
      case "TextCaption":
      case "RichText":
        validateTextBlock(comp, cPrefix, errors); break;
      case "Footer":
        validateFooter(comp, screen, allScreenIds, cPrefix, errors); break;
      case "Form":
        break; // already flagged above (nested form)
      default:
        if (!ALL_VALID_FORM_TYPES.includes(comp.type))
          errors.push(`${cPrefix}: Unknown component type "${comp.type}"`);
    }
  });

  if (footerCount === 0) {
    warnings.push(`${prefix}: Form has no Footer button`);
  }
}

// ─── Component validators ───

function validateTextBlock(comp, prefix, errors) {
  const limitMap = {
    TextHeading: LIMITS.TEXT_HEADING_MAX,
    TextSubheading: LIMITS.TEXT_SUBHEADING_MAX,
    TextBody: LIMITS.TEXT_BODY_MAX,
    TextCaption: LIMITS.TEXT_CAPTION_MAX,
    RichText: LIMITS.RICH_TEXT_MAX,
    Image: 0,
  };

  if (comp.type === "Image") {
    validateImage(comp, prefix, errors);
    return;
  }

  const maxLen = limitMap[comp.type] || LIMITS.TEXT_HEADING_MAX;
  if (!comp.text) {
    errors.push(`${prefix}: ${comp.type} is missing text`);
  } else if (comp.text.length > maxLen) {
    errors.push(`${prefix}: ${comp.type} exceeds ${maxLen} chars`);
  }
}

function validateImage(comp, prefix, errors) {
  if (!comp.src) {
    errors.push(`${prefix}: Image is missing "src" URL`);
  } else if (!/^https?:\/\/.+/.test(comp.src)) {
    errors.push(`${prefix}: Image src must be a valid URL`);
  }
  if (comp.height !== undefined && comp.height !== null) {
    const h = Number(comp.height);
    if (isNaN(h) || h < LIMITS.IMAGE_HEIGHT_MIN || h > LIMITS.IMAGE_HEIGHT_MAX) {
      errors.push(`${prefix}: Image height must be ${LIMITS.IMAGE_HEIGHT_MIN}–${LIMITS.IMAGE_HEIGHT_MAX}`);
    }
  }
}

function validateTextInput(comp, prefix, errors) {
  if (comp.label && comp.label.length > LIMITS.TEXT_INPUT_LABEL_MAX)
    errors.push(`${prefix}: Label exceeds ${LIMITS.TEXT_INPUT_LABEL_MAX} chars`);
  if (comp["input-type"] && !VALID_INPUT_TYPES.includes(comp["input-type"]))
    errors.push(`${prefix}: Invalid input-type "${comp["input-type"]}". Allowed: ${VALID_INPUT_TYPES.join(", ")}`);
  if (comp.placeholder)
    errors.push(`${prefix}: "placeholder" is not allowed in TextInput — use "label" instead`);
}

function validateDropdown(comp, prefix, errors) {
  if (comp.label && comp.label.length > LIMITS.TEXT_INPUT_LABEL_MAX)
    errors.push(`${prefix}: Label exceeds ${LIMITS.TEXT_INPUT_LABEL_MAX} chars`);
  const opts = comp["data-source"] || [];
  // Rule 8: data-source required
  if (opts.length === 0)
    errors.push(`${prefix}: Dropdown must have at least one option`);
  if (opts.length > LIMITS.DROPDOWN_OPTIONS_MAX)
    errors.push(`${prefix}: Dropdown has ${opts.length} options (max ${LIMITS.DROPDOWN_OPTIONS_MAX})`);
  validateOptions(opts, prefix, errors);
}

function validateRadioButtons(comp, prefix, errors) {
  const opts = comp["data-source"] || [];
  if (opts.length === 0)
    errors.push(`${prefix}: RadioButtonsGroup must have at least one option`);
  if (opts.length > LIMITS.RADIO_OPTIONS_MAX)
    errors.push(`${prefix}: Radio group has ${opts.length} options (max ${LIMITS.RADIO_OPTIONS_MAX})`);
  validateOptions(opts, prefix, errors);
}

function validateCheckboxGroup(comp, prefix, errors) {
  const opts = comp["data-source"] || [];
  if (opts.length === 0)
    errors.push(`${prefix}: CheckboxGroup must have at least one option`);
  if (opts.length > LIMITS.CHECKBOX_OPTIONS_MAX)
    errors.push(`${prefix}: Checkbox group has ${opts.length} options (max ${LIMITS.CHECKBOX_OPTIONS_MAX})`);
  validateOptions(opts, prefix, errors);
}

// Rule 9: data-source structure
function validateOptions(opts, prefix, errors) {
  const ids = new Set();
  opts.forEach((opt, i) => {
    if (!opt.id) {
      errors.push(`${prefix}: Option ${i + 1} missing "id"`);
    } else {
      if (opt.id.length > LIMITS.OPTION_ID_MAX)
        errors.push(`${prefix}: Option ${i + 1} id exceeds ${LIMITS.OPTION_ID_MAX} chars`);
      if (ids.has(opt.id))
        errors.push(`${prefix}: Duplicate option id "${opt.id}"`);
      ids.add(opt.id);
    }
    if (!opt.title) {
      errors.push(`${prefix}: Option ${i + 1} missing "title"`);
    } else if (opt.title.length > LIMITS.OPTION_TITLE_MAX) {
      errors.push(`${prefix}: Option ${i + 1} title exceeds ${LIMITS.OPTION_TITLE_MAX} chars`);
    }
  });
}

// Rule 14: DatePicker min-date <= max-date
function validateDatePicker(comp, prefix, errors) {
  if (comp.label && comp.label.length > LIMITS.DATEPICKER_LABEL_MAX)
    errors.push(`${prefix}: Label exceeds ${LIMITS.DATEPICKER_LABEL_MAX} chars`);
  const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
  if (comp["min-date"] && !dateRegex.test(comp["min-date"]))
    errors.push(`${prefix}: min-date must be YYYY-MM-DD`);
  if (comp["max-date"] && !dateRegex.test(comp["max-date"]))
    errors.push(`${prefix}: max-date must be YYYY-MM-DD`);
  if (
    comp["min-date"] && comp["max-date"] &&
    dateRegex.test(comp["min-date"]) && dateRegex.test(comp["max-date"]) &&
    comp["min-date"] > comp["max-date"]
  ) {
    errors.push(`${prefix}: min-date cannot be after max-date`);
  }
}

function validateFooter(comp, screen, allScreenIds, prefix, errors) {
  if (!comp.label)
    errors.push(`${prefix}: Footer missing label`);
  else if (comp.label.length > LIMITS.FOOTER_LABEL_MAX)
    errors.push(`${prefix}: Footer label exceeds ${LIMITS.FOOTER_LABEL_MAX} chars`);

  const action = comp["on-click-action"];
  if (!action || !action.name) {
    errors.push(`${prefix}: Footer missing on-click-action`);
    return;
  }
  if (!["navigate", "complete"].includes(action.name)) {
    errors.push(`${prefix}: Footer action must be "navigate" or "complete"`);
    return;
  }

  // Rule 7: navigate target must exist
  if (action.name === "navigate") {
    const target = action.next?.name;
    if (!target)
      errors.push(`${prefix}: Navigate action missing target screen`);
    else if (!allScreenIds.has(target))
      errors.push(`${prefix}: Navigate target "${target}" does not exist`);
    else if (target === screen.id)
      errors.push(`${prefix}: Cannot navigate to the same screen`);

    // payload is auto-generated at export time
  }

  // Rule 5: complete only on terminal
  if (action.name === "complete" && !screen.terminal) {
    errors.push(`${prefix}: "complete" action is only allowed on terminal screens`);
  }

  // complete payload is auto-generated at export time
}

// ─── Single-value helpers (used by editor for inline hints) ───

export function validateScreenId(id, existingIds = []) {
  const errs = [];
  if (!id) { errs.push("Screen ID is required"); return errs; }
  if (id.length > LIMITS.SCREEN_ID_MAX_LEN) errs.push(`ID exceeds ${LIMITS.SCREEN_ID_MAX_LEN} chars`);
  if (!SCREEN_ID_REGEX.test(id)) errs.push("Only A-Z, a-z, _ allowed (no numbers)");
  if (existingIds.includes(id)) errs.push("ID already exists");
  return errs;
}

export function validateFieldName(name, existingNames = []) {
  const errs = [];
  if (!name) { errs.push("Field name is required"); return errs; }
  if (name.length > LIMITS.TEXT_INPUT_NAME_MAX) errs.push(`Exceeds ${LIMITS.TEXT_INPUT_NAME_MAX} chars`);
  if (!FIELD_NAME_REGEX.test(name)) errs.push("Only A-Z, a-z, 0-9, _ allowed");
  if (RESERVED_FIELD_NAMES.includes(name.toLowerCase())) errs.push(`"${name}" is reserved`);
  if (existingNames.includes(name)) errs.push("Name already used");
  return errs;
}
